import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { trackNavigation } from './ui/Breadcrumbs';

/**
 * ScrollToTop - Smart scroll management for React Router
 * 
 * Behavior:
 * - Forward navigation (clicking links, navigate()): Scroll to top + track navigation
 * - Back/Forward navigation (browser buttons, navigate(-1)): Let browser/page restore scroll position
 * - Page refresh: Scroll to top
 * - Query-only changes on same path (e.g., pagination): NO scroll, NO track
 * 
 * Also tracks internal navigation for the smart back button feature.
 */
const ScrollToTop = () => {
  const { pathname, key } = useLocation();
  const [searchParams] = useSearchParams();
  const navigationType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  const isInitialMount = useRef(true);
  const prevPathname = useRef(pathname);
  const prevSearchParams = useRef(new URLSearchParams());

  // Handle route changes
  useEffect(() => {
    // Skip on initial mount (handled separately below)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevPathname.current = pathname;
      prevSearchParams.current = new URLSearchParams(searchParams);
      return;
    }

    // navigationType:
    // - 'PUSH': Normal navigation (clicking a link, navigate('/path'))
    // - 'REPLACE': Replace navigation (navigate('/path', { replace: true }))
    // - 'POP': Back/forward navigation (browser buttons, navigate(-1))
    
    if (navigationType === 'POP') {
      // Back/forward navigation - let browser/page handle scroll restoration
      // Enable browser's scroll restoration
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
      
      // Check if there's a saved scroll position for this page
      // If so, the page component will handle restoration - don't interfere
      const hasHomeSavedScroll = sessionStorage.getItem('homeScrollY');
      const hasMarketplaceSavedScroll = sessionStorage.getItem('marketplaceScrollY');
      
      if (hasHomeSavedScroll || hasMarketplaceSavedScroll) {
        // Page will handle its own scroll restoration
        // Do nothing here
      }
      // Otherwise, browser's auto restoration will handle it
      
    } else {
      // Forward navigation (PUSH/REPLACE)
      
      // Check if this is just a query parameter change on the same path (like pagination)
      // If so, skip scroll-to-top to allow smooth "Load More" UX
      const isSamePath = pathname === prevPathname.current;
      const isOnlyPageChange = isSamePath && (() => {
        // Compare all params except 'page' - if they're the same, only page changed
        const prev = new URLSearchParams(prevSearchParams.current);
        const curr = new URLSearchParams(searchParams);
        prev.delete('page');
        curr.delete('page');
        return prev.toString() === curr.toString();
      })();
      
      if (isOnlyPageChange) {
        // Pagination change - don't scroll to top, don't track navigation
        // This enables smooth "Load More" behavior
      } else {
        // Real navigation - scroll to top and track
        trackNavigation();
        window.scrollTo(0, 0);
        
        // Disable browser scroll restoration for forward nav
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual';
        }
      }
    }
    
    prevPathname.current = pathname;
    prevSearchParams.current = new URLSearchParams(searchParams);
  }, [pathname, key, navigationType, searchParams]);

  // Handle initial page load / refresh
  useEffect(() => {
    // On initial load, clear navigation history (fresh session)
    // Don't track this as a navigation - it's the entry point
    
    // Check if there's saved scroll to restore (page refresh scenario)
    const hasHomeSavedScroll = sessionStorage.getItem('homeScrollY');
    
    if (hasHomeSavedScroll && pathname === '/') {
      // Don't scroll to top - HomePage will handle restoration
      return;
    }
    
    // On initial load, scroll to top and disable browser restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    window.scrollTo(0, 0);
    
    // Handle the case where browser tries to restore after delay
    const scrollToTop = () => {
      // Double-check we're not interfering with page restoration
      const hasHomeSaved = sessionStorage.getItem('homeScrollY');
      if (!hasHomeSaved || pathname !== '/') {
        window.scrollTo(0, 0);
      }
    };
    
    window.addEventListener('load', scrollToTop);
    
    const timeoutId = setTimeout(scrollToTop, 0);
    const timeoutId2 = setTimeout(scrollToTop, 100);
    
    return () => {
      window.removeEventListener('load', scrollToTop);
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default ScrollToTop;
